import React, { useContext, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { CONTRACTS_PAGE, ERROR_CONSTS, UserFields } from 'src/constants';
import history from 'src/history';
import { useContractBuilderAndSignerForm } from 'src/hooks/useContractBuilderAndSigner';
import { FormPage } from 'src/components/Page/FormPage';
import { BreadCrumbBase } from 'src/components/UI/BreadCrumbBase';
import {
  ContractBuilderContext,
  ContractFile,
} from 'src/context/contractBuilderContext';
import {
  ChangeEsignFlowStateAction,
  ResetESignToInitialValues,
  SetActiveComponent,
  StartSavingComponents,
  StopSavingComponents,
  UpdateClientSignatureFlowStatus,
  loadContractPages,
  setPageComponents,
} from 'src/store/signaturePage/actions';
import {
  getComponentTypeUsingContractType,
  getContractInputValueUsingComponentType,
  hasValidClientComponents,
} from 'src/utils/SignatureUtils';
import { RootState } from 'src/store';
import {
  alertSnackbar,
  openAuthModal,
  setFormPageChanged,
} from 'src/store/ui/actions';
import {
  AddContractTemplateInput,
  FieldType,
  InputType,
  useAddContractTemplateMutation,
  useGetContractTemplatesQuery,
  useGetContractsQuery,
  useSignContractMutation,
  useUpdateContractTemplateMutation,
} from 'src/services/api';
import { ContractPageTabs } from './ContractsTablePage';
import { ContractField, PredefinedAutofillKeys } from 'src/entities/Contract';
import { RouteContext } from 'src/context';
import {
  ESignatureFlowStatus,
  SignaturePageComponent,
} from 'src/store/signaturePage/types';
import { scrollToComponent } from '../Signature/ClientSignatureIndicatorPanel';
import { useSignatureRequestComponent } from '../Signature/SignatureSidebar/useSignatureRequestComponent';
import { loadClientCustomFields } from 'src/store/clients/actions';
import { CustomFieldOption } from 'src/store/clients/types';
import { LocaleDateFormatter } from 'src/components/Formatters';
import { areArraysEqual } from 'src/utils/ArrayUtils';
import { ensureUnreachable } from 'src/utils/CommonUtils';
import { ContractDetailsPageView } from './ContractDetailsPage';
import {
  getPageComponentsRelativeToPage,
  textToBase64Image,
} from 'src/utils/ContractUtils';
import { AuthModal } from '../Auth';
import { AUTH_STATES } from 'src/constants/authConsts';
import { usePageTitle } from 'src/hooks/usePageTitle';

export interface ContractTemplateForm {
  title: string;
  fileKey: string;
}

type ContractSubmitPageQueryParams = {
  contractId: string;
  templateId: string;
};

export function extractLastVariablePart(inputString: string) {
  const regex = /\{{2}(.+?)\.{1}(.+?)\}{2}/; // Regular expression to match text within double curly braces with a dot separator
  const match = inputString.match(regex);
  if (match && match.length >= 3) {
    return match.at(2); // Extracted last part of the variable name
  }
  return null;
}

/**
 * Finds a custom field in the provided customFields object based on the target key.
 * @param {Record<string, CustomFieldOption>} customFields - The custom fields object.
 * @param {string} targetKey - The target key to search for.
 */
export function findCustomFieldByKey(
  customFields: Record<string, CustomFieldOption>,
  targetKey: string,
) {
  for (const id in customFields) {
    if (customFields[id].key === targetKey) {
      return customFields[id];
    }
  }
  return null;
}

/**
 * This component represents the contract management form, which allows internal user to create
 * contract template and client to sign the contract.
 *
 * @returns {JSX.Element} The JSX element rendering the contract management and signing form.
 */
export const ContractBuilderAndSignerForm = () => {
  const dispatch = useDispatch();

  const {
    isClient,
    pageComponents,
    userId,
    signatureFlowStatus,
    users,
    customFields,
    isClientCustomFieldsLoaded,
    isClientCustomFieldsLoading,
    isFormPageChanged,
    pageImages,
  } = useSelector(
    (state: RootState) => ({
      isClient: state.user.isClient,
      pageComponents: state.signaturePage.pageComponents,
      userId: state.user.id,
      signatureFlowStatus: state.signaturePage.signatureFlowStatus,
      users: state.users.users,
      customFields: state.clients.clientCustomFields?.additionalFields || {},
      isClientCustomFieldsLoading: state.clients.isClientCustomFieldsLoading,
      isClientCustomFieldsLoaded: state.clients.isClientCustomFieldsLoaded,
      isFormPageChanged: state.ui.isFormPageChanged,
      pageImages: state.signaturePage.pageImages,
    }),
    shallowEqual,
  );

  const { handleRequestReceiverInfo } = useSignatureRequestComponent();

  const { query } = useContext(RouteContext);
  const { contractId, templateId } = query as ContractSubmitPageQueryParams;
  const { pageTitle } = usePageTitle(CONTRACTS_PAGE.label, 'contracts');

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // Indicates if the client has filled all the signature fields and is on the end signature screen on mobile devices
  const isClientOnEndSignatureOnMobile =
    isClient &&
    isSmallScreen &&
    signatureFlowStatus === ESignatureFlowStatus.END_SIGNAURE;

  const { data: contracts } = useGetContractsQuery(
    {
      recipientId: userId,
    },
    {
      skip: !isClient,
    },
  );

  const { data: templates } = useGetContractTemplatesQuery();

  const [addContractTemplate] = useAddContractTemplateMutation();
  const [updateContractTemplate] = useUpdateContractTemplateMutation();
  const [signContract] = useSignContractMutation();

  // State to hold information about the uploaded PDF file:
  // - 'fileKey' - The key to access the file from S3.
  // - 'pageFileKeys' - An array of keys for page images saved in S3.
  const [contractPdfInfo, setContractPdfInfo] = useState<ContractFile>({
    fileKey: '',
    pageFileKeys: [],
    identityId: '',
  });

  // State to hold a reference to the placeholder element.
  // This element is used as an anchor for positioning signature popups.
  const [placeholderElement, setPlaceholderElement] =
    useState<HTMLButtonElement | null>(null);

  const [initialPageComponents, setInitialPageComponents] = useState<
    SignaturePageComponent[]
  >([]);

  const navigateToContractsPage = () =>
    history.push(`${CONTRACTS_PAGE.path}?view=${ContractPageTabs.Templates}`);

  const navigateToContractDetailsPage = (id: string) =>
    history.push(
      `${CONTRACTS_PAGE.path}/details?templateId=${id}&view=${ContractDetailsPageView.SubmissionRequests}`,
    );

  /**
   * Handles the save action for creating a contract template.
   * Redirects to the contracts table page upon successful template creation.
   * @param {ContractTemplateForm} values - The form values containing template information.
   */
  const handleCreateContractTemplate = async (values: ContractTemplateForm) => {
    // Check if there is at least one valid client signature input to create a template
    if (!hasValidClientComponents(pageComponents)) {
      dispatch(
        alertSnackbar({
          errorMessage: ERROR_CONSTS.AddComponentForClient,
        }),
      );
      return;
    }

    const contractFieldPromises: Array<Promise<ContractField>> =
      pageComponents.map(async (p) => ({
        id: p.key,
        label: p.label,
        // TODO: remove these nullish coallesing operators when we fix the types
        page: p.page ?? 0,
        type: p.fieldType!,
        inputType: p.inputType,
        isOptional: Boolean(p.isOptional),
        value: getContractInputValueUsingComponentType(p),
        encodedImgValue:
          // If the field type is Signature or Initials, use the provided base64 value
          p.fieldType === FieldType.Signature ||
          p.fieldType === FieldType.Initials
            ? p.value // Base64 value for signature and initials inputs
            : await textToBase64Image(p), // Otherwise, convert the text value into Base64
        autoFillField: p.autoFillField,
        bounds: {
          x: p.xPosition,
          y: p.yPosition,
          height: p.height,
          width: p.width,
          pageWidth: p.pageWidth ?? 0,
          pageHeight: p.pageHeight ?? 0,
        },
      }));

    const allContractFields = await Promise.all(contractFieldPromises);

    // Prepare the request body for creating the contract template
    const requestBody: AddContractTemplateInput = {
      name: values.title,
      fileKey: contractPdfInfo.fileKey,
      identityId: contractPdfInfo.identityId,
      fields: allContractFields,
    };

    // Send a request to add the contract template
    const result = await addContractTemplate(requestBody);

    if ('data' in result) {
      navigateToContractDetailsPage(result.data.id);
    }
  };

  const handleUpdateContractTemplate = async (values: ContractTemplateForm) => {
    // Check if there is at least one valid client signature input to create a template
    if (!hasValidClientComponents(pageComponents)) {
      dispatch(
        alertSnackbar({
          errorMessage: ERROR_CONSTS.AddComponentForClient,
        }),
      );
      return;
    }

    const selectedTemplate = templates?.find((t) => t.id === templateId);

    if (!selectedTemplate) return;

    const contractFieldPromises: Array<Promise<ContractField>> =
      pageComponents.map(async (p) => ({
        id: p.key,
        label: p.label,
        page: p.page ?? 0,
        type: p.fieldType!,
        inputType: p.inputType,
        isOptional: Boolean(p.isOptional),
        value: getContractInputValueUsingComponentType(p),
        encodedImgValue:
          // If the field type is Signature or Initials, use the provided base64 value
          p.fieldType === FieldType.Signature ||
          p.fieldType === FieldType.Initials
            ? p.value // Base64 value for signature and initials inputs
            : await textToBase64Image(p), // Otherwise, convert the text value into Base64
        autoFillField: p.autoFillField,
        bounds: {
          x: p.xPosition,
          y: p.yPosition,
          height: p.height,
          width: p.width,
          pageWidth: p.pageWidth ?? 0,
          pageHeight: p.pageHeight ?? 0,
        },
      }));

    const allContractFields = await Promise.all(contractFieldPromises);

    const requestBody = {
      id: templateId,
      body: {
        ...selectedTemplate,
        name: values.title,
        fields: allContractFields,
      },
    };

    await updateContractTemplate(requestBody);
  };

  /**
   * This method initiates the signing flow for clients on mobile devices
   */
  const startSigningFlowOnMobile = () => {
    dispatch(ChangeEsignFlowStateAction({ startedEsignFlow: true }));
    dispatch(
      UpdateClientSignatureFlowStatus(ESignatureFlowStatus.FILL_SIGNATURE),
    );
    const clientComponents = pageComponents.filter((c) =>
      c.componentType.startsWith('request'),
    );

    // Select the first client input for signing
    if (clientComponents.length > 0) {
      const firstClientComponent = clientComponents.at(0);
      if (firstClientComponent) {
        dispatch(SetActiveComponent(firstClientComponent.key));
        handleRequestReceiverInfo(firstClientComponent);
        scrollToComponent(firstClientComponent.key);
      }
    }
  };

  /**
   * If the e-signature flow status is START_SIGNATURE, initiates the signing flow for clients on mobile devices.
   * If the e-signature flow status is END_SIGNATURE, handles the completion of client signature request.
   */
  const handleClientAction = async () => {
    if (signatureFlowStatus === ESignatureFlowStatus.START_SIGNATURE) {
      startSigningFlowOnMobile();
      return;
    }
    if (signatureFlowStatus === ESignatureFlowStatus.END_SIGNAURE) {
      dispatch(StartSavingComponents());

      const initialPageComponentsMap = initialPageComponents.reduce(
        (acc, component) => {
          acc[component.key] = component;
          return acc;
        },
        {} as Record<string, SignaturePageComponent>,
      );

      // Restore the adjusted dimensions and position of the page components
      // for the mobile sign contract flow back to their original state
      // before sending the values to the API.
      const components = pageComponents.map((component) => ({
        ...component,
        xPosition: initialPageComponentsMap[component.key]?.xPosition,
        yPosition: initialPageComponentsMap[component.key]?.yPosition,
        width: initialPageComponentsMap[component.key]?.width,
        height: initialPageComponentsMap[component.key]?.height,
      }));

      // Signature components are placed relative to the entire PDF, but
      // while generating the PDF in the backend, we require the y position of the
      // component relative to the page it is placed on.
      const componentsRelativeToPdfPage = await getPageComponentsRelativeToPage(
        pageImages,
        components,
      );

      const contractFieldPromises: Array<Promise<ContractField>> =
        componentsRelativeToPdfPage.map(async (p) => ({
          id: p.key,
          label: p.label,
          page: p.page ?? 0,
          type: p.fieldType!,
          inputType: p.inputType,
          isOptional: Boolean(p.isOptional),
          value: getContractInputValueUsingComponentType(p),
          encodedImgValue:
            // If the field type is Signature or Initials, use the provided base64 value
            p.fieldType === FieldType.Signature ||
            p.fieldType === FieldType.Initials
              ? p.value // Base64 value for signature and initials inputs
              : await textToBase64Image(p), // Otherwise, convert the text value into Base64
          autoFillField: p.autoFillField,
          bounds: {
            x: p.xPosition,
            y: p.yPosition,
            height: p.height,
            width: p.width,
            pageWidth: p.pageWidth ?? 0,
            pageHeight: p.pageHeight ?? 0,
          },
        }));

      const selectedContract = contracts?.find((c) => c.id === contractId);
      if (!selectedContract) return;

      const allContractFields = await Promise.all(contractFieldPromises);

      const requestBody = {
        id: selectedContract.id,
        body: {
          ...selectedContract,
          fields: allContractFields,
        },
      };
      await signContract(requestBody);
      dispatch(StopSavingComponents());
    }
  };

  const handleSave = async (values: ContractTemplateForm) => {
    if (!isClient) {
      if (templateId) {
        await handleUpdateContractTemplate(values);
      } else {
        await handleCreateContractTemplate(values);
      }
    }

    await handleClientAction();
  };

  const onCancel = () => {
    const redirectionPath = templateId
      ? `${CONTRACTS_PAGE.path}/details?templateId=${templateId}&view=${ContractDetailsPageView.SubmissionRequests}`
      : `${CONTRACTS_PAGE.path}?view=${ContractPageTabs.Templates}`;
    dispatch(
      openAuthModal(AUTH_STATES.CONFIRM_PAGE_CHANGE_MODAL, redirectionPath),
    );
    if (templateId) {
      dispatch(setPageComponents(initialPageComponents));
      return;
    }

    if (isClientOnEndSignatureOnMobile) {
      startSigningFlowOnMobile();
    }
  };

  const getPredefinedAutofillInputValue = (
    key: PredefinedAutofillKeys,
    clientInfoFields: UserFields,
  ) => {
    switch (key) {
      case PredefinedAutofillKeys.FirstName:
        return clientInfoFields.givenName;
      case PredefinedAutofillKeys.FullName:
        return `${clientInfoFields.givenName} ${clientInfoFields.familyName}`;
      case PredefinedAutofillKeys.LastName:
        return clientInfoFields.familyName;
      case PredefinedAutofillKeys.Email:
        return clientInfoFields.email;
      case PredefinedAutofillKeys.ShareDate: {
        const selectedContract = contracts?.find((c) => c.id === contractId);
        return selectedContract?.createdAt
          ? LocaleDateFormatter({ value: String(selectedContract.createdAt) })
          : null;
      }
      default:
        return ensureUnreachable(key);
    }
  };

  /**
   * Gets the autofill input value for a given contract field.
   * @param {ContractField} field - The contract field containing the autoFillField property.
   * @returns {string | null} - The autofill input value or null if not found.
   */
  const findAutofillInputValue = (field: ContractField) => {
    if (!field?.autoFillField) return null;

    const key = extractLastVariablePart(field.autoFillField);
    const clientInfoFields = users.find((u) => u.id === userId)?.fields;

    if (!clientInfoFields || !key) return null;

    if (
      Object.values(PredefinedAutofillKeys).includes(
        key as PredefinedAutofillKeys,
      )
    ) {
      return getPredefinedAutofillInputValue(
        key as PredefinedAutofillKeys,
        clientInfoFields,
      );
    }

    const customField = findCustomFieldByKey(customFields, key);

    if (!customField || !clientInfoFields?.customFields) return null;

    // Handle the case when the custom field type is 'multiSelect'.
    // Here we will separate multiple options using ","
    if (customField.type === 'multiSelect' && customField?.options?.length) {
      const tagOptions = customField.options;
      const tags = clientInfoFields.customFields[customField.id];
      const tagLabels: string[] = [];
      // For clients without multi-select custom fields value assigned, tags will be null, so we need to return early.
      if (!tagOptions || !tags) return '';
      tags.forEach((tag: string) => {
        const tagOption = tagOptions.find((i) => i.id === tag);
        if (tagOption) tagLabels.push(tagOption.label);
      });
      return tagLabels.join(', ');
    }

    return clientInfoFields.customFields[customField.id];
  };

  const getInitialSignautreInputValue = (field: ContractField) => {
    if (field.inputType === InputType.AutoFill) {
      return findAutofillInputValue(field);
    }

    if (
      field.type === FieldType.Signature ||
      field.type === FieldType.Initials
    ) {
      return field.encodedImgValue;
    }

    return field.value;
  };

  /**
   * Load clients custom fields
   */
  useEffect(() => {
    if (!isClientCustomFieldsLoading && !isClientCustomFieldsLoaded) {
      dispatch(loadClientCustomFields());
    }
  }, [isClientCustomFieldsLoading, isClientCustomFieldsLoaded]);

  /**
   * For client users, this effect retrieves contract info based on the contractId
   * available in the query parameters. It then loads the contract page images using the
   * "pageKeys" and "identityId" from the selected contract data.
   */
  useEffect(() => {
    // Wait for custom fields to load before adding signature page components.
    // This is necessary because we need custom fields information to obtain values for autofill fields.
    if (!contractId && !isClientCustomFieldsLoaded) return;
    const selectedContract = contracts?.find((c) => c.id === contractId);
    if (selectedContract) {
      const formattedContractFields: Array<SignaturePageComponent> = (
        selectedContract.fields || []
      )
        .map((field: ContractField) => {
          const value = getInitialSignautreInputValue(field);

          // Remove AutoFill inputs for which the client doesn't have a value
          if (!value && field.inputType === InputType.AutoFill) return null;

          return {
            fieldType: field.type,
            inputType: field.inputType,
            autoFillField: field.autoFillField,
            xPosition: field.bounds.x,
            yPosition: field.bounds.y,
            label: field.label,
            receiverUserId: selectedContract.recipientId,
            height: field.bounds.height,
            width: field.bounds.width,
            isAgree: false,
            key: field.id,
            resizedHeight: field.bounds.resizedHeight,
            resizedWidth: field.bounds.resizedWidth,
            value,
            page: field.page,
            componentType: getComponentTypeUsingContractType(
              field,
              field.inputType === InputType.Client,
            ),
            name: field.value,
            isOptional: field.isOptional,
          };
        })
        .filter(Boolean); // Filter out null values

      setInitialPageComponents(formattedContractFields);
      setContractPdfInfo({
        pageFileKeys: selectedContract.pageKeys,
        fileKey: selectedContract.fileKey,
        identityId: selectedContract.identityId,
      });
    }
  }, [contractId, contracts, isClientCustomFieldsLoaded]);

  /**
   * For internal users, this effect retrieves contract info based on the templateId
   * available in the query parameters. It then loads the contract page images using the
   * "pageKeys" and "identityId" from the selected template data.
   */
  useEffect(() => {
    if (!templateId) return;
    const selectedTemplate = templates?.find((t) => t.id === templateId);
    if (selectedTemplate) {
      const formattedContractFields: SignaturePageComponent[] = (
        selectedTemplate.fields || []
      ).map((field: ContractField) => ({
        fieldType: field.type,
        inputType: field.inputType,
        autoFillField: field.autoFillField,
        name: field.value,
        xPosition: field.bounds.x,
        yPosition: field.bounds.y,
        label: field.label,
        height: field.bounds.height,
        width: field.bounds.width,
        isAgree: false,
        key: field.id,
        resizedHeight: field.bounds.resizedHeight,
        resizedWidth: field.bounds.resizedWidth,
        value:
          field.type === FieldType.Signature ||
          field.type === FieldType.Initials
            ? field.encodedImgValue
            : field.value,
        page: field.page,
        componentType: getComponentTypeUsingContractType(
          field,
          field.inputType !== InputType.Fixed,
        ),
        isOptional: field.isOptional,
      }));

      setInitialPageComponents(formattedContractFields);
      setContractPdfInfo({
        pageFileKeys: selectedTemplate.pageKeys,
        fileKey: selectedTemplate.fileKey,
        identityId: selectedTemplate.identityId,
      });
    }
  }, [templateId]);

  /* When the user is redirected to other pages (for example, when the contract creation flow is canceled or completed),
   * we want the signaturePage reducer to reset to its initial state.
   * This ensures that when they return to create a new contract, they begin with a clean slate.
   */
  useEffect(() => {
    if (contractPdfInfo?.pageFileKeys?.length) {
      dispatch(loadContractPages(contractPdfInfo, initialPageComponents));
    }

    return () => {
      dispatch(ResetESignToInitialValues());
    };
  }, [contractPdfInfo, initialPageComponents]);

  // Define the states and methods to be passed down using the `ContractBuilderContext`.
  const contextValue: ContractBuilderContext = useMemo(
    () => ({
      placeholderElement,
      contractPdfInfo,
      setPlaceholderElement,
      setContractPdfInfo,
    }),
    [placeholderElement, contractPdfInfo],
  );

  const breadcrumbItems = useMemo(() => {
    const selectedTemplate = templates?.find((t) => t.id === templateId);

    return [
      {
        label: pageTitle,
      },
      ...(selectedTemplate?.name
        ? [
            {
              label: selectedTemplate?.name,
            },
          ]
        : [
            {
              label: 'New contract',
            },
          ]),
    ];
  }, []);
  const getSubmitActionLabel = React.useMemo(() => {
    if (!isClient) {
      if (templateId) return 'Save';
      return 'Create template';
    }
    if (signatureFlowStatus === ESignatureFlowStatus.START_SIGNATURE)
      return 'Start';
    return 'Finish and send';
  }, [signatureFlowStatus]);

  const getCancelActionLabel = React.useMemo(() => {
    if (isClientOnEndSignatureOnMobile) {
      return 'Back to edit';
    }

    return 'Cancel';
  }, [isClientOnEndSignatureOnMobile, signatureFlowStatus, templateId]);

  const initialFormValues: ContractTemplateForm = useMemo(() => {
    if (templateId) {
      const selectedTemplate = templates?.find((t) => t.id === templateId);

      return {
        title: selectedTemplate?.name || '',
        fileKey: selectedTemplate?.fileKey || '',
      };
    }

    return {
      title: '',
      fileKey: '',
    };
  }, [templateId, templates]);

  const isEditFormChanged =
    templateId && !areArraysEqual(initialPageComponents, pageComponents);

  /**
   * Ensures a confirmation prompt is shown before the user exits the page
   * in case there are any unsaved changes in the form.
   */
  useEffect(() => {
    dispatch(setFormPageChanged(Boolean(isEditFormChanged)));
    return () => {
      // Ensures the form change state is reset to false once the component is unmounted.
      dispatch(setFormPageChanged(false));
    };
  }, [isEditFormChanged]);

  const isSubmitActionButtonDisabled = useMemo(() => {
    // Disable the submit button during the client sign flow if the signature flow has been initiated
    if (isClient) {
      return signatureFlowStatus === ESignatureFlowStatus.FILL_SIGNATURE;
    }

    // Disable the save button for editing contract templates when there are no changes in the form.
    return Boolean(templateId) && !isFormPageChanged;
  }, [signatureFlowStatus, templateId, isFormPageChanged, isClient]);

  const useContractBuilderSignerForm = () =>
    useContractBuilderAndSignerForm({ onPageSave: handleClientAction });

  return (
    <ContractBuilderContext.Provider value={contextValue}>
      <AuthModal />
      <FormPage
        useFormHook={useContractBuilderSignerForm}
        headerVariant="bottomBorder"
        BreadcrumbsComponent={
          !isClient ? <BreadCrumbBase items={breadcrumbItems} /> : undefined
        }
        resetFormOnCancel={false}
        cancelLabel={getCancelActionLabel}
        actionLabel={getSubmitActionLabel}
        handleCancel={onCancel}
        shouldShowPageAction
        handleSave={handleSave}
        initialFormValue={initialFormValues}
        hideFormToolbar={isClient && !isSmallScreen}
        title="eSignature request"
        hideCancelAction={isClient}
        cancelDisabled={
          (isClient &&
            signatureFlowStatus === ESignatureFlowStatus.FILL_SIGNATURE) ||
          !isFormPageChanged
        }
        showBackButton={
          isClient &&
          isSmallScreen &&
          signatureFlowStatus !== ESignatureFlowStatus.START_SIGNATURE
        }
        onBackButtonClick={navigateToContractsPage}
        disableSubmit={isSubmitActionButtonDisabled}
      />
    </ContractBuilderContext.Provider>
  );
};
